import { supabase } from '@/lib/supabase/client';
import type { Ayah } from '@/types/domain';

/**
 * Get all ayahs in a surah, ordered by ayah_number.
 *
 * Reads: quran_core.ayahs (read-only).
 *
 * We use `.select('*')` and cast the result. supabase-js's column-string
 * inference can resolve to `GenericStringError` when the Database type
 * comes from a hand-written types.gen.ts; `*` is unambiguous and the
 * cast is sound because the runtime returns every column on the row.
 */
export async function getAyahsBySurah(surahId: number): Promise<Ayah[]> {
  const { data, error } = await supabase
    .schema('quran_core')
    .from('ayahs')
    .select('*')
    .eq('surah_id', surahId)
    .order('ayah_number', { ascending: true });

  if (error) throw error;
  return (data ?? []) as Ayah[];
}

/**
 * Get a single ayah by its (surah_id, ayah_number) natural key.
 *
 * Reads: quran_core.ayahs (read-only).
 */
export async function getAyah(
  surahId: number,
  ayahNumber: number,
): Promise<Ayah | null> {
  const { data, error } = await supabase
    .schema('quran_core')
    .from('ayahs')
    .select('*')
    .eq('surah_id', surahId)
    .eq('ayah_number', ayahNumber)
    .maybeSingle();

  if (error) throw error;
  return (data as Ayah | null) ?? null;
}
